import React, { useEffect, useState } from 'react';
import { useInput, useResourceContext } from "ra-core";
import { CommonInputProps, InputHelperText, Labeled } from "react-admin";
import { isValidData, isValidJSON, PriceCalculator } from '../price-calculator';
import { styled } from "@mui/material/styles";
import { FormHelperText } from "@mui/material";

export const PriceCalculatorInput = (props: PriceCalculatorInputProps) => {
    const {
        className,
        source,
        defaultValue,
        disabled,
        fullWidth,
        helperText,
        label,
        readOnly,
        required,
        validate,
        base64,
        ...rest
    } = props;

    const resource = useResourceContext(props);

    const validateData = () => (data: any) => {
        let value = base64 ? atob(field.value) : field.value;

        if (value == '') {
        return undefined;
        }

        if (!isValidJSON(value)) {
        return 'Incorrect output format!';
        }

        let parsedData = JSON.parse(value);

        if (required && parsedData.length == 0) {
        return 'Required';
        }

        if (!isValidData(parsedData)) {
        return 'Make sure all inputs are filled.';
        }
        
        return undefined;
    }

    const validators = validate && validate.length ? [validate] : validate ?? [];

    const {
        id,
        field,
        fieldState,
        formState: { isSubmitted },
    } = useInput({ ...rest, source, defaultValue, validate: [...validators as [], validateData()] });

    const { error, invalid, isTouched } = fieldState;
    const [value, setValue] = useState(field.value);


    const parseValue = (value) => {
        return base64 ? btoa(unescape(encodeURIComponent(value))) : value;
    };
    
    const resolveValue = () => {
        return base64 ? decodeURIComponent(escape(atob(value))) : value;
    };

    const handleUpdate = (value) => {
        const data = parseValue(value);
    
        field.onChange(data);
        field.onBlur();
    };

    return (
        <Labeled
          isRequired={required}
          label={label}
          id={`${id}-label`}
          color={fieldState?.invalid ? "error" : undefined}
          source={source}
          resource={resource}
          fullWidth={fullWidth}
          sx={{
            ".RaLabeled-label": {
              marginLeft: "14px",
            },
          }}
        >
          <Root className={className}>
            <PriceCalculator onEditorChange={handleUpdate} value={resolveValue()}/>
            <FormHelperText
              sx={{width: "100%", marginTop: "14px", paddingLeft: "14px"}}
              className={
                (isTouched || isSubmitted) && invalid
                  ? "tinymce-content-input-error"
                  : ""
              }
              error={(isTouched || isSubmitted) && invalid}
            >
              <InputHelperText
                touched={isTouched || isSubmitted}
                error={error?.message}
                helperText={helperText}
              />
            </FormHelperText>
          </Root>
        </Labeled>
    );
};

const PREFIX = "EditorBuilderInput";
const Root = styled("div", {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
}));

PriceCalculatorInput.defaultProps = {
  base64: false,
  disabled: false,
  required: false,
  readOnly: false,
  fullWidth: false,
  defaultValue: "",
};

export type PriceCalculatorInputProps = CommonInputProps & {
    disabled?: boolean;
    readOnly?: boolean;
    base64?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    helperText?: string | React.ReactElement | false;
    className?: string;
    source: string;
};